import React from 'react';
import { includes, isEmpty } from 'lodash';
import UnavailabilityBox from './calendar-items/UnavailabilityBox';
import OrderBox from './calendar-items/OrderBox';
import HoursBorders from './views/components/HoursBorders';
import EventBox from './calendar-items/EventBox';
import { getCalendarCellLocations } from './calendar-items/CalendarCalculator';
import { CalendarItems } from './calendarWrapper/CalendarWrapper';
import { AccountData, EnrichedEventData, OrderData } from '../api';
import { unifySlot } from './UnifiedShiftAndUserAvailability';
import { UnifiedShiftsUserAvailabilitiesBox } from './calendar-items/UnifiedShiftsUserAvailabilitiesBox';
import { ResourcesBox } from './calendar-items/ResourcesBox';
import { useAuth } from '../session/InternalAuthProvider';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { useCalendar } from './CalendarContext/CalendarContext';
import DragDropProvider from './views/dragndrop/DragDropProvider';
import NoteBox from './calendar-items/NoteBox';
import { PrivateSlotAvailabilityBox } from './calendar-items/PrivateSlotAvailabilityBox';
import { useCalendarFilter } from './CalendarFilter/CalendarFilterContext';
import { getCalendarLocationIds } from './CalendarFilter/calendarLocationIds';
import { DaySummary } from './utils/calendarSummary';
import { SummaryClusterBox } from './calendar-items/SummaryClusterBox';

type CalendarDayProps = {
  dateToRender: string;
  items: CalendarItems;
  openOrder: (event: any, order: OrderData) => void;
  openEvent: (event: EnrichedEventData) => void;
  numOfDays: number;
  summary?: DaySummary;
  peakSlotCount?: number;
};
const CalendarDay = ({
  items,
  dateToRender,
  openOrder,
  openEvent,
  numOfDays,
  summary,
  peakSlotCount,
}: CalendarDayProps) => {
  const { authState } = useAuth();

  const account = authState.account as unknown as AccountData;
  const { readOnly } = UseAuthPermissions();
  const { selectedUsersIds, users } = useCalendar();
  const { selectedOrderPageIds } = useCalendarFilter();

  if (!items) {
    return <HoursBorders />;
  }

  // Check if account has scheduler configs for resources view
  const shouldShowResources =
    account?.shared_resources?.scheduler_configs?.length &&
    account?.shared_resources?.scheduler_configs?.length > 0 &&
    numOfDays === 1;
  const unavailabilitiesByDate = items.unavailabilities[dateToRender] || [];
  const notesByDate = items.notes[dateToRender] || [];
  const filteredNotes = notesByDate.filter((x) => !x.all_day);
  const filteredUnavailabilities = unavailabilitiesByDate?.filter((u) => !u.all_day) ?? [];
  // Collapsed items are counted in the summary, so they must not be drawn as cards or go into a shift box.
  const filteredEvents = (items.events[dateToRender] || []).filter((e) => !summary?.collapsedIds.has(e.id));
  const filteredOrders = (items.orders[dateToRender] || []).filter((o) => !summary?.collapsedIds.has(o.id));
  const calendarLocationIds = getCalendarLocationIds(account, selectedOrderPageIds);
  const isLocationVisible = (locationId: string | null | undefined) =>
    !calendarLocationIds || !locationId || calendarLocationIds.includes(locationId);
  const filteredShifts =
    items.shifts[dateToRender]
      ?.filter((x) => (isEmpty(selectedUsersIds) ? x : includes(selectedUsersIds, x.employee_id)))
      .filter((x) => isLocationVisible(users.find((user) => user.user_id === x.employee_id)?.location_id)) || [];
  const userAvailabilities =
    items.calendarUserAvailabilities[dateToRender]
      ?.filter((x) => (isEmpty(selectedUsersIds) ? x : includes(selectedUsersIds, x.user_id)))
      .filter((x) => isLocationVisible(x.location_id)) || [];
  const filteredPrivateSlotAvailabilities = items.privateSlotAvailabilities[dateToRender] || [];

  const { unifiedSlot, filteredOrdersLeft, filteredEventsLeft } = unifySlot(
    filteredShifts,
    userAvailabilities,
    filteredOrders,
    filteredEvents,
  );

  const cssResult = getCalendarCellLocations(
    filteredOrdersLeft,
    filteredEventsLeft,
    filteredUnavailabilities,
    unifiedSlot,
    filteredNotes,
    filteredPrivateSlotAvailabilities,
    summary?.clusters ?? [],
  );
  const shouldNotDragNDrop = readOnly;

  if (shouldShowResources) {
    return (
      <DragDropProvider orders={filteredOrders} shiftAndAvailabilities={unifiedSlot} events={filteredEvents}>
        <HoursBorders />
        <ResourcesBox
          orders={filteredOrders}
          unavailabilities={filteredUnavailabilities}
          openOrder={openOrder}
          css={{
            width: 100,
            left: 0,
            top: 0,
            height: 600, // TODO: Calculate properly
          }}
        />
      </DragDropProvider>
    );
  }

  return (
    <DragDropProvider orders={filteredOrders} shiftAndAvailabilities={unifiedSlot} events={filteredEvents}>
      <HoursBorders />
      {filteredOrdersLeft.map((order) => {
        const css = cssResult.find((x) => x.itemId === order.id);
        return (
          <OrderBox
            viewType="day"
            key={order.id}
            order={order}
            css={css}
            openOrder={openOrder}
            shouldNotDragNDrop={shouldNotDragNDrop}
          />
        );
      })}
      {filteredEventsLeft.map((event) => {
        const css = cssResult.find((x) => x.itemId === event.id);
        return (
          <EventBox
            viewType="day"
            key={event.id}
            event={event}
            css={css}
            openEvent={openEvent}
            shouldNotDragNDrop={shouldNotDragNDrop}
          />
        );
      })}
      {filteredUnavailabilities.map((unavailability) => {
        const css = cssResult.find((x) => x.itemId === unavailability.id);
        return <UnavailabilityBox viewType="day" key={unavailability.id} unavailability={unavailability} css={css} />;
      })}
      {filteredNotes.map((note) => {
        const css = cssResult.find((x) => x.itemId === note.id);
        return <NoteBox viewType="day" key={note.id} note={note} css={css} />;
      })}
      {unifiedSlot.map((shiftAndAvailability) => {
        const css = cssResult.find((x) => x.itemId === shiftAndAvailability.id);
        return (
          <UnifiedShiftsUserAvailabilitiesBox
            viewType="day"
            key={shiftAndAvailability.id}
            shiftsAndUserAvailability={shiftAndAvailability}
            css={css}
            date={dateToRender}
            openOrder={openOrder}
            openEvent={openEvent}
          />
        );
      })}
      {filteredPrivateSlotAvailabilities.map((privateSlotAvailability) => {
        const css = cssResult.find((x) => x.itemId === privateSlotAvailability.id);
        return (
          <PrivateSlotAvailabilityBox
            viewType="day"
            key={privateSlotAvailability.id}
            privateSlotAvailability={privateSlotAvailability}
            css={css}
          />
        );
      })}
      {summary?.clusters.map((cluster) => (
        <SummaryClusterBox
          key={cluster.id}
          cluster={cluster}
          css={cssResult.find((x) => x.itemId === cluster.id)}
          peakSlotCount={peakSlotCount ?? 1}
          date={dateToRender}
          items={items}
          openOrder={openOrder}
          openEvent={openEvent}
        />
      ))}
    </DragDropProvider>
  );
};

export default CalendarDay;
